import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/Button";
import { ApiError } from "@/lib/api/client";
import { facilitiesApi } from "./api";
import { useFacilityRole } from "./useFacilityRole";
import type { Item, ItemCategory, ItemStatus } from "./types";

// View selector controls the BASE set of items the rest of the
// controls operate on. Status chips + search refine within that.
type ItemView = "all" | "needs_attention" | "unpositioned";
type ItemSort = "default" | "severity" | "updated" | "name";

// Severity ordering for the "Status severity" sort. Higher number =
// surfaces earlier. Matches the colour-tone gradient too.
const SEVERITY_RANK: Record<ItemStatus, number> = {
  out_of_service: 4,
  major: 3,
  minor: 2,
  unknown: 1,
  ok: 0,
};

// Statuses that count as "needs attention" in the quick-view tab.
const ATTENTION_STATUSES: readonly ItemStatus[] = [
  "minor",
  "major",
  "out_of_service",
];

function statusLabel(s: ItemStatus, t: (k: string) => string): string {
  return t(`items.status.${s}`);
}

const STATUS_TONE: Record<ItemStatus, string> = {
  ok: "bg-emerald-100 text-emerald-700",
  minor: "bg-amber-100 text-amber-700",
  major: "bg-orange-100 text-orange-700",
  out_of_service: "bg-rose-100 text-rose-700",
  unknown: "bg-slate-100 text-slate-600",
};

const STATUS_OPTIONS: ItemStatus[] = [
  "ok",
  "minor",
  "major",
  "out_of_service",
  "unknown",
];

export function ItemsPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const facilityId = Number(id);
  const [showItemForm, setShowItemForm] = useState(false);

  const itemsQ = useQuery({
    queryKey: ["items", facilityId],
    queryFn: () => facilitiesApi.listItems(facilityId),
    enabled: !Number.isNaN(facilityId),
  });
  const categoriesQ = useQuery({
    queryKey: ["categories", facilityId],
    queryFn: () => facilitiesApi.listCategories(facilityId),
    enabled: !Number.isNaN(facilityId),
  });

  const { isOwner } = useFacilityRole(facilityId);
  const allItems = itemsQ.data?.results ?? [];
  const categories = categoriesQ.data?.results ?? [];
  const unpositioned = allItems.filter(
    (i) => i.latitude == null || i.longitude == null,
  ).length;
  const needsAttention = allItems.filter((i) =>
    ATTENTION_STATUSES.includes(i.current_status),
  ).length;

  // Listing controls.
  const [view, setView] = useState<ItemView>("all");
  const [sort, setSort] = useState<ItemSort>("default");
  const [activeStatuses, setActiveStatuses] = useState<Set<ItemStatus>>(
    () => new Set(),
  );
  const [search, setSearch] = useState("");

  // Stage 1: apply view-level base predicate.
  const baseItems = useMemo(() => {
    if (view === "needs_attention") {
      return allItems.filter((i) =>
        ATTENTION_STATUSES.includes(i.current_status),
      );
    }
    if (view === "unpositioned") {
      return allItems.filter(
        (i) => i.latitude == null || i.longitude == null,
      );
    }
    return allItems;
  }, [allItems, view]);

  // Status chip counts are computed from the base set, so the user sees
  // what's available within the current view rather than across-the-board
  // numbers that would mislead.
  const statusCounts = useMemo(() => {
    const counts: Record<ItemStatus, number> = {
      ok: 0,
      minor: 0,
      major: 0,
      out_of_service: 0,
      unknown: 0,
    };
    for (const it of baseItems) counts[it.current_status] += 1;
    return counts;
  }, [baseItems]);

  // Stage 2: refine by status chips + free-text search.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return baseItems.filter((i) => {
      if (activeStatuses.size > 0 && !activeStatuses.has(i.current_status)) {
        return false;
      }
      if (q) {
        const hay =
          i.name.toLowerCase() +
          " " +
          (i.description ?? "").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [baseItems, activeStatuses, search]);

  // Stage 3: sort. Always grouped by category in the render layer;
  // this sort applies within each category section.
  const sorted = useMemo(() => {
    const copy = [...filtered];
    switch (sort) {
      case "severity":
        copy.sort(
          (a, b) =>
            SEVERITY_RANK[b.current_status] - SEVERITY_RANK[a.current_status] ||
            a.name.localeCompare(b.name),
        );
        break;
      case "updated":
        copy.sort(
          (a, b) =>
            new Date(b.updated_at ?? b.created_at).getTime() -
            new Date(a.updated_at ?? a.created_at).getTime(),
        );
        break;
      case "name":
        copy.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "default":
      default:
        copy.sort(
          (a, b) =>
            (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
            a.name.localeCompare(b.name),
        );
        break;
    }
    return copy;
  }, [filtered, sort]);

  // Stage 4: group by category for rendering. Items without a category
  // collect under a synthetic "Uncategorised" bucket at the end.
  const groups = useMemo(
    () => groupItemsByCategory(sorted, categories, t),
    [sorted, categories, t],
  );

  const toggleStatus = (s: ItemStatus) =>
    setActiveStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });

  const clearFilters = () => {
    setActiveStatuses(new Set());
    setSearch("");
    setView("all");
  };

  const hasActiveFilters =
    view !== "all" || activeStatuses.size > 0 || search.trim().length > 0;

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 text-sm">
          <span className="rounded bg-slate-900 px-3 py-1 font-medium text-white">
            {t("items.list")}
          </span>
          <Link
            to="map"
            className="rounded px-3 py-1 text-slate-600 hover:bg-slate-50"
          >
            {t("items.map")}
          </Link>
        </div>
        <div className="flex items-center gap-2">
          {isOwner && (
            <Link
              to={`/facilities/${facilityId}/walk`}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-500"
            >
              📍 {t("items.walk_facility")}
            </Link>
          )}
          {isOwner && (
            <Button onClick={() => setShowItemForm((s) => !s)}>
              {showItemForm ? t("common.cancel") : t("items.new")}
            </Button>
          )}
        </div>
      </div>
      {showItemForm && (
        <ItemForm
          facilityId={facilityId}
          categories={categories}
          onCreated={() => setShowItemForm(false)}
        />
      )}

      {allItems.length > 0 && (
        <ListControls
          view={view}
          onViewChange={setView}
          sort={sort}
          onSortChange={setSort}
          activeStatuses={activeStatuses}
          onToggleStatus={toggleStatus}
          statusCounts={statusCounts}
          search={search}
          onSearchChange={setSearch}
          counts={{
            all: allItems.length,
            needsAttention,
            unpositioned,
          }}
        />
      )}

      {allItems.length === 0 ? (
        <EmptyState facilityId={facilityId} canWalk={isOwner} />
      ) : sorted.length === 0 ? (
        <FilteredEmptyState onClear={clearFilters} hasFilters={hasActiveFilters} />
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <CategorySection
              key={g.key}
              title={g.title}
              items={g.items}
              facilityId={facilityId}
              categories={categories}
              editable={isOwner}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// ─── Category grouping ────────────────────────────────────────────────

type ItemGroup = {
  key: string;
  title: string;
  items: Item[];
};

function groupItemsByCategory(
  items: Item[],
  categories: ItemCategory[],
  t: (k: string) => string,
): ItemGroup[] {
  const byId = new Map<number, ItemCategory>(
    categories.map((c) => [c.id, c]),
  );
  // Preserve the source category ordering — categories have their own
  // `sort_order, name` ordering on the backend. Append any categories
  // referenced by items but not in the list (defensive).
  const order: number[] = categories.map((c) => c.id);
  const bucket = new Map<number | "uncat", Item[]>();
  for (const it of items) {
    const key = it.category ?? "uncat";
    if (!bucket.has(key)) bucket.set(key, []);
    bucket.get(key)!.push(it);
    if (typeof key === "number" && !order.includes(key)) order.push(key);
  }
  const groups: ItemGroup[] = [];
  for (const cid of order) {
    const items = bucket.get(cid);
    if (!items || items.length === 0) continue;
    groups.push({
      key: `c${cid}`,
      title: byId.get(cid)?.name ?? `#${cid}`,
      items,
    });
  }
  const uncat = bucket.get("uncat");
  if (uncat && uncat.length > 0) {
    groups.push({
      key: "uncat",
      title: t("items.uncategorised"),
      items: uncat,
    });
  }
  return groups;
}

function CategorySection({
  title,
  items,
  facilityId,
  categories,
  editable,
}: {
  title: string;
  items: Item[];
  facilityId: number;
  categories: ItemCategory[];
  editable: boolean;
}) {
  return (
    <div className="rounded-md border border-slate-200 bg-white">
      <header className="flex items-center justify-between border-b border-slate-100 px-4 py-2 text-xs uppercase tracking-wide text-slate-500">
        <span className="font-semibold text-slate-700">{title}</span>
        <span>{items.length}</span>
      </header>
      <ul className="divide-y divide-slate-100">
        {items.map((it) => (
          <ItemRow
            key={it.id}
            item={it}
            facilityId={facilityId}
            categories={categories}
            editable={editable}
          />
        ))}
      </ul>
    </div>
  );
}

// ─── Controls ─────────────────────────────────────────────────────────

function ListControls({
  view,
  onViewChange,
  sort,
  onSortChange,
  activeStatuses,
  onToggleStatus,
  statusCounts,
  search,
  onSearchChange,
  counts,
}: {
  view: ItemView;
  onViewChange: (v: ItemView) => void;
  sort: ItemSort;
  onSortChange: (s: ItemSort) => void;
  activeStatuses: Set<ItemStatus>;
  onToggleStatus: (s: ItemStatus) => void;
  statusCounts: Record<ItemStatus, number>;
  search: string;
  onSearchChange: (v: string) => void;
  counts: { all: number; needsAttention: number; unpositioned: number };
}) {
  const { t } = useTranslation();
  const views: { id: ItemView; label: string; count: number }[] = [
    { id: "all", label: t("items.view_all"), count: counts.all },
    {
      id: "needs_attention",
      label: t("items.view_needs_attention"),
      count: counts.needsAttention,
    },
    {
      id: "unpositioned",
      label: t("items.view_unpositioned"),
      count: counts.unpositioned,
    },
  ];
  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      {/* Quick-view tabs */}
      <div className="flex flex-wrap gap-1">
        {views.map((v) => (
          <button
            key={v.id}
            type="button"
            onClick={() => onViewChange(v.id)}
            aria-pressed={view === v.id}
            className={
              "rounded-md px-3 py-1 text-sm font-medium transition-colors " +
              (view === v.id
                ? "bg-slate-900 text-white"
                : "bg-white text-slate-700 hover:bg-slate-100")
            }
          >
            {v.label}
            <span
              className={
                "ml-1.5 rounded px-1.5 text-[10px] font-medium " +
                (view === v.id
                  ? "bg-white/20 text-white"
                  : "bg-slate-100 text-slate-600")
              }
            >
              {v.count}
            </span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {/* Status chips */}
        <div className="flex flex-wrap gap-1.5">
          {STATUS_OPTIONS.map((s) => {
            const count = statusCounts[s];
            const active = activeStatuses.has(s);
            const disabled = count === 0 && !active;
            return (
              <button
                key={s}
                type="button"
                onClick={() => onToggleStatus(s)}
                disabled={disabled}
                aria-pressed={active}
                className={
                  "rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors " +
                  (active
                    ? STATUS_TONE[s] + " ring-2 ring-offset-1 ring-slate-400"
                    : disabled
                      ? "bg-slate-100 text-slate-400 opacity-60"
                      : STATUS_TONE[s] + " hover:opacity-80")
                }
              >
                {statusLabel(s, t)} <span className="opacity-70">{count}</span>
              </button>
            );
          })}
        </div>

        <div className="ml-auto flex items-center gap-2">
          {/* Search */}
          <input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("items.search_placeholder")}
            className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500/30"
          />

          {/* Sort */}
          <label className="flex items-center gap-1 text-xs text-slate-600">
            <span className="hidden sm:inline">{t("items.sort_label")}</span>
            <select
              value={sort}
              onChange={(e) => onSortChange(e.target.value as ItemSort)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500/30"
            >
              <option value="default">{t("items.sort_default")}</option>
              <option value="severity">{t("items.sort_severity")}</option>
              <option value="updated">{t("items.sort_updated")}</option>
              <option value="name">{t("items.sort_name")}</option>
            </select>
          </label>
        </div>
      </div>
    </div>
  );
}

function FilteredEmptyState({
  onClear,
  hasFilters,
}: {
  onClear: () => void;
  hasFilters: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 text-center">
      <p className="text-sm text-slate-500">{t("items.no_matches")}</p>
      {hasFilters && (
        <button
          type="button"
          onClick={onClear}
          className="mt-2 text-sm font-medium text-brand-600 hover:underline"
        >
          {t("items.clear_filters")}
        </button>
      )}
    </div>
  );
}

function EmptyState({
  facilityId,
  canWalk,
}: {
  facilityId: number;
  canWalk: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 text-center">
      <p className="text-sm text-slate-500">{t("items.empty")}</p>
      {canWalk && (
        <>
          <Link
            to={`/facilities/${facilityId}/walk`}
            className="mt-4 inline-block rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-500"
          >
            {t("items.empty_cta_walk")}
          </Link>
          <p className="mx-auto mt-3 max-w-md text-xs text-slate-500">
            {t("items.empty_cta_walk_blurb")}
          </p>
        </>
      )}
    </div>
  );
}

function ItemRow({
  item,
  facilityId,
  categories,
  editable,
}: {
  item: Item;
  facilityId: number;
  categories: ItemCategory[];
  editable: boolean;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  if (editing && editable) {
    return (
      <li className="p-4">
        <ItemEditForm
          item={item}
          facilityId={facilityId}
          categories={categories}
          onClose={() => setEditing(false)}
        />
      </li>
    );
  }
  // Maintainers see a static row (no click-to-edit affordance) since they
  // can't mutate items — only file maintenance completions.
  const body = (
    <>
      <div className="truncate font-medium">{item.name}</div>
      {item.description && (
        <div className="truncate text-sm text-slate-500">
          {item.description}
        </div>
      )}
    </>
  );
  return (
    <li className="flex items-center justify-between p-4">
      {editable ? (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="min-w-0 flex-1 text-left"
        >
          {body}
        </button>
      ) : (
        <div className="min-w-0 flex-1">{body}</div>
      )}
      <span
        className={`ml-3 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONE[item.current_status]}`}
      >
        {statusLabel(item.current_status, t)}
      </span>
    </li>
  );
}

function ItemEditForm({
  item,
  facilityId,
  categories,
  onClose,
}: {
  item: Item;
  facilityId: number;
  categories: ItemCategory[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  type Values = {
    name: string;
    description: string;
    category: string;
    current_status: ItemStatus;
  };
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<Values>({
    defaultValues: {
      name: item.name,
      description: item.description ?? "",
      category: item.category != null ? String(item.category) : "",
      current_status: item.current_status,
    },
  });
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (v: Values) =>
      facilitiesApi.updateItem(item.id, {
        name: v.name,
        description: v.description,
        category: v.category ? Number(v.category) : null,
        current_status: v.current_status,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["items", facilityId] });
      onClose();
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) setError("Could not save changes.");
      else setError("Could not save changes.");
    },
  });

  const remove = useMutation({
    mutationFn: () => facilitiesApi.destroyItem(item.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["items", facilityId] });
      onClose();
    },
    onError: () => setError("Could not delete item."),
  });

  return (
    <form
      className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-4"
      onSubmit={handleSubmit((v) => {
        setError(null);
        save.mutate(v);
      })}
      noValidate
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium">
            {t("settings.name", "Name")}
          </label>
          <input
            className="input"
            {...register("name", { required: t("common.required", "Required") })}
          />
          {errors.name && (
            <p className="text-xs text-rose-600">{errors.name.message}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium">
            {t("public.status.title", "Status")}
          </label>
          <select className="input" {...register("current_status")}>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {statusLabel(s, t)}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium">
          {t("settings.description")}
        </label>
        <textarea rows={2} className="input" {...register("description")} />
      </div>
      <div>
        <label className="block text-sm font-medium">
          {t("walk.no_category", "Category")}
        </label>
        <select className="input" {...register("category")}>
          <option value="">{t("items.no_category")}</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      {error && <p className="text-sm text-rose-600">{error}</p>}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => {
            if (
              confirm(t("items.delete_confirm", { name: item.name }))
            ) {
              remove.mutate();
            }
          }}
          disabled={remove.isPending}
          className="text-rose-600 hover:bg-rose-50"
        >
          {t("common.delete")}
        </Button>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={isSubmitting || save.isPending || !isDirty}
          >
            {save.isPending ? t("settings.saving") : t("common.save", "Save")}
          </Button>
        </div>
      </div>
    </form>
  );
}

function ItemForm({
  facilityId,
  categories,
  onCreated,
}: {
  facilityId: number;
  categories: ItemCategory[];
  onCreated: () => void;
}) {
  const qc = useQueryClient();
  type Values = {
    name: string;
    description: string;
    category: string;
    new_category: string;
  };
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<Values>({
    defaultValues: { name: "", description: "", category: "", new_category: "" },
  });
  const categoryValue = watch("category");
  const [error, setError] = useState<string | null>(null);

  const newCatMutation = useMutation({
    mutationFn: (name: string) =>
      facilitiesApi.createCategory({ facility: facilityId, name }),
    onSuccess: (cat) => {
      qc.invalidateQueries({ queryKey: ["categories", facilityId] });
      return cat;
    },
  });

  const mutation = useMutation({
    mutationFn: async (values: Values) => {
      let categoryId: number | null = values.category
        ? Number(values.category)
        : null;
      if (values.category === "__new__" && values.new_category.trim()) {
        const cat = await newCatMutation.mutateAsync(values.new_category.trim());
        categoryId = cat.id;
      }
      return facilitiesApi.createItem({
        name: values.name,
        description: values.description,
        facility: facilityId,
        category: categoryId,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["items", facilityId] });
      reset();
      onCreated();
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) setError("Could not create item.");
    },
  });

  return (
    <form
      className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-4"
      onSubmit={handleSubmit((v) => {
        setError(null);
        mutation.mutate(v);
      })}
      noValidate
    >
      <div>
        <label htmlFor="item-name" className="block text-sm font-medium">
          Name
        </label>
        <input
          id="item-name"
          className="input"
          {...register("name", { required: "Required" })}
        />
        {errors.name && (
          <p className="text-xs text-rose-600">{errors.name.message}</p>
        )}
      </div>
      <div>
        <label htmlFor="item-desc" className="block text-sm font-medium">
          Description
        </label>
        <textarea
          id="item-desc"
          rows={2}
          className="input"
          {...register("description")}
        />
      </div>
      <div>
        <label className="block text-sm font-medium">Category</label>
        <select className="input" {...register("category")}>
          <option value="">No category</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
          <option value="__new__">+ New category…</option>
        </select>
        {categoryValue === "__new__" && (
          <input
            className="input mt-2"
            placeholder="Category name"
            {...register("new_category", {
              required: "Category name required",
            })}
          />
        )}
      </div>
      {error && <p className="text-sm text-rose-600">{error}</p>}
      <div className="flex justify-end">
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? "Creating…" : "Create"}
        </Button>
      </div>
    </form>
  );
}
